import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  SelectChangeEvent,
  Container,
  Paper,
  Chip,
  LinearProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardHeader,
  Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  useCampaignQuery,
  useCampaignStatusQuery,
  useUpdateCampaignMutation,
  useUpdateCampaignStatusMutation,
  useSnackbar,
  useUnsavedChangesWarning,
} from '../hooks';
import { LoadingIndicator, ErrorDisplay, TargetingConditionChip, CopyButton } from './common';
import {
  CampaignFormData,
  TargetingCondition,
  TestProtocol,
  Campaign,
  CampaignStatus as CampaignStatusType,
} from '../types';
import CampaignEditForm from './CampaignEditForm';
import { useAuth } from '../contexts/AuthContext';
import SaveIcon from '@mui/icons-material/Save';
import { formatSecondsToHMS } from '../utils';

// Helper component for campaign overview card
const CampaignOverviewCard: React.FC<{ campaign: Campaign }> = ({ campaign }) => (
  <Card elevation={1} sx={{ transition: 'none', '&:hover': { transform: 'none', boxShadow: 1 } }}>
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <SettingsIcon />
        </Avatar>
      }
      title="Campaign Overview"
      subheader="Basic information and settings"
      titleTypographyProps={{ variant: 'h6', fontWeight: 'medium' }}
    />
    <CardContent>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Description:</strong> {campaign.description ?? 'No description provided'}
          </Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Campaign ID
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {campaign.campaign_id}
            </Typography>
            <CopyButton text={campaign.campaign_id} tooltip="Copy Campaign ID" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Output Format
          </Typography>
          <Typography variant="body2">{campaign.output_format?.toUpperCase() ?? 'CSV'}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Start Date
          </Typography>
          <Typography variant="body2">
            {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : 'Not set'}
          </Typography>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            End Date
          </Typography>
          <Typography variant="body2">
            {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'Not set'}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Helper component for collection status card
const CollectionStatusCard: React.FC<{
  campaign: Campaign;
  campaignStatus: CampaignStatusType | undefined;
}> = ({ campaign, campaignStatus }) => (
  <Card elevation={1} sx={{ transition: 'none', '&:hover': { transform: 'none', boxShadow: 1 } }}>
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: 'success.main' }}>
          <ScheduleIcon />
        </Avatar>
      }
      title="Collection Status"
      subheader="How data collection is progressing"
      titleTypographyProps={{ variant: 'h6', fontWeight: 'medium' }}
    />
    <CardContent>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Current Status
        </Typography>
        {renderStatusChip(campaign.status)}
      </Box>
      {/* Show data collected from either campaignStatus or campaign, with preference for campaignStatus */}
      {(campaignStatus?.data_collected_seconds !== undefined ||
        campaign.data_collected_seconds !== undefined) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Data Collected
          </Typography>
          <Typography variant="h6" color="primary">
            {formatSecondsToHMS(
              campaignStatus?.data_collected_seconds ?? campaign.data_collected_seconds ?? 0
            )}
          </Typography>
        </Box>
      )}
      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(campaignStatus?.last_updated || campaign.last_updated) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Last Updated
          </Typography>
          <Typography variant="body2">
            {new Date(campaignStatus?.last_updated ?? campaign.last_updated ?? '').toLocaleString()}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// Helper component for data collection protocols card
const DataCollectionProtocolsCard: React.FC<{ campaign: Campaign }> = ({ campaign }) => (
  <Card elevation={1} sx={{ transition: 'none', '&:hover': { transform: 'none', boxShadow: 1 } }}>
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: 'warning.main' }}>
          <DataUsageIcon />
        </Avatar>
      }
      title="What Data is Collected"
      subheader="Protocols define what data is collected, conditions select the users and devices"
      titleTypographyProps={{ variant: 'h6', fontWeight: 'medium' }}
    />
    <CardContent>
      {campaign.structured_test_protocols && campaign.structured_test_protocols.length > 0 ? (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {campaign.structured_test_protocols.map((protocol, index) => (
              <Box
                key={protocol.name ?? `protocol-${index}`}
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  {protocol.name}
                </Typography>

                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 'auto',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      '& .MuiAccordionSummary-content': { margin: '8px 0' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" color="primary">
                        Applies to users and devices matching:
                      </Typography>
                      {renderProtocolConditions(
                        protocol.condition_names,
                        campaign.targeting_conditions
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, bgcolor: 'white', borderRadius: 1 }}>
                    {renderConditionDetails(
                      protocol.condition_names,
                      campaign.targeting_conditions
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 'auto',
                      bgcolor: 'white',
                      borderRadius: 1,
                      '& .MuiAccordionSummary-content': { margin: '8px 0' },
                    }}
                  >
                    <Typography variant="body2" color="primary">
                      View Technical Details (JSON Protocol)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, bgcolor: 'white', borderRadius: 1 }}>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        backgroundColor: 'grey.100',
                        p: 1.5,
                        borderRadius: 1,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    >
                      {protocol.protocol_content}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography color="text.secondary">Protocols and conditions are not defined.</Typography>
      )}
    </CardContent>
  </Card>
);

// Helper function to determine chip color based on campaign status
const getStatusColor = (status: string | undefined): 'success' | 'error' | 'default' => {
  if (status === 'enabled') return 'success';
  if (status === 'disabled') return 'error';
  return 'default';
};

// Helper function to render status chip
const renderStatusChip = (status: string | undefined, sx?: object) => (
  <Chip
    label={status ? status.toUpperCase() : 'UNKNOWN'}
    color={getStatusColor(status)}
    sx={{ fontWeight: 'bold', ...sx }}
  />
);

// Helper function to render protocol condition chips
const renderProtocolConditions = (
  conditionNames: string[] | undefined,
  targetingConditions: TargetingCondition[] | undefined
) => {
  if (!conditionNames || conditionNames.length === 0) {
    return (
      <Chip
        label="All users"
        sx={{
          bgcolor: 'grey.300',
          color: 'text.secondary',
          fontSize: '0.75rem',
          height: '24px',
        }}
        size="small"
      />
    );
  }

  return conditionNames.map((conditionName, condIndex) => {
    const matchingCondition = targetingConditions?.find(tc => tc.name === conditionName);
    if (!matchingCondition) return null;

    return (
      <TargetingConditionChip
        key={conditionName ?? condIndex}
        condition={matchingCondition}
        sx={{
          fontSize: '0.75rem',
          height: '24px',
        }}
        size="small"
      />
    );
  });
};

// Helper function to render condition details
const renderConditionDetails = (
  conditionNames: string[] | undefined,
  targetingConditions: TargetingCondition[] | undefined
) => {
  if (!conditionNames || conditionNames.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        All users (no specific targeting)
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {conditionNames.map((conditionName, condIndex) => {
        const matchingCondition = targetingConditions?.find(tc => tc.name === conditionName);
        if (!matchingCondition) return null;

        return (
          <Box
            key={conditionName ?? condIndex}
            sx={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              gap: 1.5,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              alignItems: 'start',
            }}
          >
            <TargetingConditionChip
              condition={matchingCondition}
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              size="small"
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: 'white',
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                fontSize: '0.8rem',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              {matchingCondition.expression}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading: loading, error, refetch } = useCampaignQuery(id);
  const { data: campaignStatus, isLoading: statusLoading } = useCampaignStatusQuery(id);
  const updateCampaignMutation = useUpdateCampaignMutation();
  const updateStatusMutation = useUpdateCampaignStatusMutation();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { canEditCampaigns } = useAuth();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // Unsaved changes warning
  const { ConfirmationDialog } = useUnsavedChangesWarning({
    when: editMode && hasUnsavedChanges,
    message:
      'You have unsaved changes to this campaign. Are you sure you want to leave without saving?',
    title: 'Unsaved Campaign Changes',
  });

  // Form data state for editing
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    output_format: 'csv',
    targeting_conditions: [], // Added
    structured_test_protocols: [], // Added
    retention_days: undefined,
    // Status is NOT part of the edit form data
  });

  // Update formData when campaign data loads or changes
  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name ?? '',
        description: campaign.description ?? '',
        start_date: campaign.start_date ?? '',
        end_date: campaign.end_date ?? '',
        output_format: campaign.output_format ?? 'csv',
        targeting_conditions: campaign.targeting_conditions ?? [], // Added
        structured_test_protocols: campaign.structured_test_protocols ?? [], // Added
        retention_days: campaign.retention_days ?? undefined,
        // Status is not included here
      });
      // Reset unsaved changes when new data loads
      setHasUnsavedChanges(false);
    }
  }, [campaign]);

  // Reset unsaved changes when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      setHasUnsavedChanges(false);
    }
  }, [editMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };
  const handleTargetingConditionsChange = (conditions: TargetingCondition[]) => {
    setFormData(prev => ({ ...prev, targeting_conditions: conditions }));
    setHasUnsavedChanges(true);
  };

  const handleStructuredProtocolsChange = (protocols: TestProtocol[]) => {
    setFormData(prev => ({ ...prev, structured_test_protocols: protocols }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!id || !campaign) return;
    try {
      setSaving(true);
      await updateCampaignMutation.mutateAsync({ campaignId: id, data: formData });
      showSnackbar('Campaign updated successfully', 'success');
      setEditMode(false);
      setHasUnsavedChanges(false);
    } catch (err) {
      showSnackbar(`Failed to update campaign: ${(err as Error).message}`, 'error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Handle toggling the campaign status (enabled/disabled)
  const handleToggleStatus = () => {
    if (id && campaign && (campaign.status === 'enabled' || campaign.status === 'disabled')) {
      setSaving(true);
      const newStatus = campaign.status === 'enabled' ? 'disabled' : 'enabled';
      updateStatusMutation
        .mutateAsync({ campaignId: id, status: newStatus })
        .finally(() => setSaving(false));
    }
  };

  if ((loading || statusLoading) && !campaign) {
    // Show loading only initially
    return <LoadingIndicator message="Loading campaign details..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error.message ?? 'An error occurred while loading campaign'}
        retryAction={() => refetch()}
        backRoute="/campaigns"
        backLabel="Back to Campaigns"
      />
    );
  }

  if (!campaign) {
    return (
      <ErrorDisplay
        error="Campaign not found"
        backRoute="/campaigns"
        backLabel="Back to Campaigns"
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            {campaign.name}
          </Typography>
          <Box>
            {canEditCampaigns() && (
              <Button variant="outlined" onClick={() => setEditMode(!editMode)} sx={{ mr: 1 }}>
                {editMode ? 'Cancel Edit' : 'Edit Campaign'}
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate('/campaigns')}>
              Back to Campaigns
            </Button>
          </Box>
        </Box>

        {/* Status and Toggle Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          {renderStatusChip(campaign.status, { mr: 2 })}
          {canEditCampaigns() &&
            (campaign.status === 'enabled' || campaign.status === 'disabled') && (
              <Button
                variant="contained"
                onClick={handleToggleStatus}
                disabled={saving}
                color={campaign.status === 'enabled' ? 'secondary' : 'primary'}
              >
                {campaign.status === 'enabled' ? 'Disable' : 'Enable'} Campaign
              </Button>
            )}
          {/* Top Save button for edit mode */}
          {editMode && canEditCampaigns() && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              startIcon={<SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Box>

        {saving && <LinearProgress sx={{ mb: 2 }} />}

        {editMode && canEditCampaigns() ? (
          <CampaignEditForm
            initialData={formData}
            onInputChange={handleInputChange}
            onTargetingConditionsChange={handleTargetingConditionsChange} // Added
            onStructuredProtocolsChange={handleStructuredProtocolsChange} // Added
            saving={saving}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Campaign Overview Card */}
            <CampaignOverviewCard campaign={campaign} />

            {/* Data Collection Status Card */}
            <CollectionStatusCard campaign={campaign} campaignStatus={campaignStatus} />

            {/* Data Collection Protocols Card */}
            <DataCollectionProtocolsCard campaign={campaign} />
          </Box>
        )}

        {/* Save button for edit mode */}
        {editMode && canEditCampaigns() && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            sx={{ mt: 3 }}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Paper>
      {snackbar.open && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
      <ConfirmationDialog />
    </Container>
  );
};

export default CampaignDetail;
